import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Crown, Radio, Cpu, MessageCircle, ExternalLink, UserPlus, Wallet, Send, ShieldCheck } from "lucide-react";
import { waLink, track } from "../components/join/pixel";
import { useAuth } from "../context/AuthContext";
import { VIP_DEFAULT } from "../lib/vipDefaults";
import { VipResults } from "../components/vip/VipResults";

const ICONS = [Radio, Cpu, Crown];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  [UserPlus, "Broker account kholein", "Niche diye gaye partner broker link se MT5 account banayein"],
  [Wallet, "Account fund karein", "Apne capital ke hisaab se deposit karein"],
  [Send, "Admin ko DM karein", "Apna MT5 ID WhatsApp pe bhejein — VIP access activate hoga"],
];

function BrokerCard({ b }) {
  const open = async () => {
    track("Lead", { channel: "broker", broker: b.name });
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.get(`${API}/go/${b.slug}?utm_source=vip_page`);
      w.location.href = data.redirect;
    } catch { w.location.href = b.referral_link || b.account_url; }
  };
  return (
    <div className="surface p-5 flex flex-col gap-3" data-testid={`vip-broker-${b.slug}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-white/5 flex items-center justify-center font-head font-bold text-gold">{b.name?.[0]}</div>
        <div>
          <div className="text-white font-semibold">{b.name}</div>
          <div className="text-[11px] text-slate-500">{[b.platform, ...(b.products || [])].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      {b.regulation && <div className="text-xs text-slate-400 flex gap-1.5"><ShieldCheck size={14} className="text-bull shrink-0" />{b.regulation}</div>}
      <button onClick={open} data-testid={`vip-broker-open-${b.slug}`} className="mt-auto flex items-center justify-center gap-2 py-2.5 border border-gold text-gold text-sm font-semibold hover:bg-[color:var(--gold)] hover:text-black transition-colors duration-200">
        Account kholein <ExternalLink size={14} />
      </button>
    </div>
  );
}

export default function VipServices() {
  const [cfg, setCfg] = useState({ brand: "Fateh FX", loading: true });
  const [brokers, setBrokers] = useState([]);
  const { user } = useAuth();
  const home = user ? (user.role === "client" ? "/portal" : user.role === "team_member" ? "/team-portal" : "/dashboard") : "/login";
  useEffect(() => {
    document.title = "VIP Signals & Algo | Fateh FX";
    axios.get(`${API}/public/join-config`).then(({ data }) => setCfg(data)).catch(() => setCfg({ brand: "Fateh FX" }));
    axios.get(`${API}/brokers/public`).then(({ data }) => setBrokers(data)).catch(() => {});
  }, []);
  const vp = cfg.vip_page || VIP_DEFAULT;
  if (cfg.loading) return <div className="min-h-screen" style={{ background: "var(--app-bg)" }} data-testid="vip-loading" />;
  const dm = (what) => waLink(cfg.wa_chat_number, `Hi ${cfg.brand} admin 👋 Mujhe ${what} join karna hai. Details bhejiye 🙏`);

  return (
    <div className="min-h-screen grain" style={{ background: "var(--app-bg)" }} data-testid="vip-page">
      <header className="max-w-6xl mx-auto px-5 pt-6 flex items-center gap-2">
        <img src="/icons/icon-192.png" alt="" className="w-8 h-8" />
        <span className="font-head font-bold text-white">Fateh FX</span>
        <Link to={home} className="ml-auto text-xs text-slate-400 hover:text-white transition-colors duration-200" data-testid="vip-login">{user ? "← Back to portal" : "Login"}</Link>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-14 pb-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="text-[11px] uppercase tracking-[0.25em] text-gold flex items-center gap-2"><Crown size={13} />Premium services</div>
        <h1 className="font-head font-bold text-white text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] max-w-3xl" data-testid="vip-title">{vp.title}{vp.subtitle && <><br /><span className="text-gold">{vp.subtitle}</span></>}</h1>
        <p className="text-slate-400 mt-5 max-w-xl text-sm sm:text-base whitespace-pre-line" data-testid="vip-intro">{vp.intro}</p>
        <a href={dm("VIP Signals / Algo")} target="_blank" rel="noreferrer" onClick={() => track("Contact", { channel: "vip_dm" })} data-testid="vip-dm-hero"
          className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-black bg-[#25D366] hover:brightness-110 hover:-translate-y-0.5 transition-[filter,transform] duration-200">
          <MessageCircle size={18} />DM Admin on WhatsApp
        </a>
      </section>

      <section className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-4" data-testid="vip-services">
        {vp.services.map((s, i) => { const Icon = ICONS[i % ICONS.length]; return (
          <div key={i} className="surface p-6 sm:p-8 flex flex-col" data-testid={`vip-service-${i}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gold text-black flex items-center justify-center"><Icon size={22} /></div>
              <div><div className="font-head text-xl text-white">{s.t}</div><div className="text-[11px] uppercase tracking-widest text-slate-500">{s.tag}</div></div>
            </div>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
              {s.pts.map((p, j) => <li key={j} className="flex gap-2"><span className="text-gold">◆</span>{p}</li>)}
            </ul>
            <a href={dm(s.t)} target="_blank" rel="noreferrer" data-testid={`vip-dm-service-${i}`}
              className="mt-6 flex items-center justify-center gap-2 py-3 border border-[#25D366] text-[#25D366] text-sm font-semibold hover:bg-[#25D366] hover:text-black transition-colors duration-200">
              <MessageCircle size={16} />{s.t} ke liye DM Admin
            </a>
          </div>
        ); })}
      </section>

      <VipResults />

      <section className="max-w-6xl mx-auto px-5 py-14" data-testid="vip-steps">
        <h2 className="font-head text-white text-base md:text-lg uppercase tracking-widest mb-5">Join karne ke 3 steps</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map(([Icon, t, d], i) => (
            <div key={t} className="surface p-5 relative">
              <div className="absolute top-4 right-5 font-mono text-3xl text-white/10">0{i + 1}</div>
              <Icon size={20} className="text-gold" />
              <div className="text-white font-semibold mt-3 text-sm">{t}</div>
              <div className="text-xs text-slate-500 mt-1">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-20" data-testid="vip-brokers">
        <h2 className="font-head text-white text-base md:text-lg uppercase tracking-widest mb-5">Partner brokers</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brokers.map((b) => <BrokerCard key={b.id} b={b} />)}
          {!brokers.length && <div className="text-sm text-slate-500" data-testid="vip-brokers-empty">Broker links ke liye admin ko DM karein.</div>}
        </div>
        <p className="text-[11px] text-slate-600 mt-6 max-w-2xl">Trading me risk hota hai. Past performance future results ki guarantee nahi hai. Sirf utna invest karein jitna aap afford kar sakein.</p>
      </section>
    </div>
  );
}
